import clsx from 'clsx';
import { IntegrationSettingsRouter } from 'components/pages/Integrations/IntegrationSettingsRouter';
import { WorkspaceType } from 'hooks/globalApi';
import { PaymentMode, useGetWorkspaceByIdQuery } from 'hooks/internalApi';
import { useFlagEnabledWithOverrides } from 'hooks/useFlagEnabledWithOverrides';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import { useWorkspaceId } from 'hooks/useWorkspace';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import { useLocation, useRoute } from 'wouter';

import { List, ListItem, Marker } from '@fragment/ui/src/components/List';
import { LoadingText } from '@fragment/ui/src/components/LoadingText';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';

import { useBillingTabEnabled } from '../../../../hooks/useBillingTabEnabled';
import { useUserInfo } from '../../../../hooks/useUserInfo';
import { makePath } from '../../../../utils/routes';
import { useWorkspace } from '../../../../wrappers/WorkspaceProvider/WorkspaceProvider';
import { LoadingTextSuspense } from '../../../LoadingTextSuspense/LoadingTextSuspense';
import { ApiClientsSettingsForm } from './ApiClientsSettingsForm';
import { BillingForm } from './BillingForm';
import { DataExportsSettingsForm } from './DataExportsSettingsForm';
import { MembersList } from './MembersList';
import { PaymentAccountsSettingsForm } from './PaymentAccountsSettingsForm';
import { UpdateWorkspaceSettingsForm } from './UpdateWorkspaceSettingsForm';
import { Preferences, Profile } from './UserSettings';

export const WORKSPACE_SETTINGS_MODAL_URL_PREFIX = '/projects/settings';

// Note: These tab slugs are user-facing, since we construct the /go/ URLs
// from these values. Choose them wisely.
export const WorkspaceSettingsTabs = [
  'workspace',
  'api-clients',
  'members',
  'data-exports',
  'payment-accounts',
  'billing',
  'user',
  'integrations',
] as const;
export type WorkspaceSettingsTabsType = (typeof WorkspaceSettingsTabs)[number];

type WorkspaceSettingsModalProps = {
  defaultTab?: WorkspaceSettingsTabsType;
  handleClose: VoidFunction;
};

export const WorkspaceSettingsModal = ({
  defaultTab = 'workspace',
  handleClose,
}: WorkspaceSettingsModalProps) => {
  const { refreshUserState } = useUserInfo();
  const { workspace } = useWorkspace();
  const [activeTab, setActiveTab] =
    useState<WorkspaceSettingsTabsType>(defaultTab);
  const base = makePath(['w', workspace?.id ?? '']);
  const [pathMatch] = useRoute(
    `${base}${WORKSPACE_SETTINGS_MODAL_URL_PREFIX}/:tab*`
  );
  const [, navigate] = useLocation();
  const billingTabEnabled = useBillingTabEnabled();
  const isLedgerPaymentsEnabled = useFlagEnabledWithOverrides(
    'dashboard-ledger-payments'
  );
  const workspaceId = useWorkspaceId();
  const { context, token } = useInternalApiContext();
  // urql requires stable query variables across renders.
  const variables = useMemo(() => ({ workspaceId }), [workspaceId]);
  const [{ data: workspaceData, error: workspaceError }] =
    useGetWorkspaceByIdQuery({
      variables,
      context,
      pause: !isLedgerPaymentsEnabled || !token,
    });
  const isPaymentAccountsEnabled =
    isLedgerPaymentsEnabled &&
    !workspaceError &&
    workspaceData?.workspace?.__typename === 'Workspace' &&
    workspaceData.workspace.id === workspaceId &&
    workspaceData.workspace.mode === PaymentMode.Production;
  const isPaymentsWorkspace = workspace?.type === WorkspaceType.Payments;
  const isFreeTierWorkspace = workspace?.type === WorkspaceType.FreeTierLedger;
  // Data exports are only for Ledger data, so payments workspaces have none,
  // and they are not part of the free tier.
  const hideDataExports = isPaymentsWorkspace || isFreeTierWorkspace;

  const availableWorkspaceTabs = useMemo(
    () =>
      WorkspaceSettingsTabs.filter(
        (tab) =>
          !(hideDataExports && tab === 'data-exports') &&
          (isPaymentAccountsEnabled || tab !== 'payment-accounts') &&
          (!isPaymentsWorkspace || tab !== 'integrations')
      ),
    [hideDataExports, isPaymentAccountsEnabled, isPaymentsWorkspace]
  );

  const modalContent = useMemo(
    () =>
      match(
        activeTab === 'payment-accounts' && !isPaymentAccountsEnabled
          ? 'workspace'
          : activeTab
      )
        .with('workspace', () => (
          <LoadingTextSuspense text="Loading">
            <div>
              <div className="pb-f4">
                <span>General</span>
              </div>
              <UpdateWorkspaceSettingsForm
                onWorkspaceUpdate={refreshUserState}
              />
            </div>
          </LoadingTextSuspense>
        ))
        .with('api-clients', () => <ApiClientsSettingsForm />)
        .with('members', () => <MembersList />)
        .with('data-exports', () => <DataExportsSettingsForm />)
        .with('payment-accounts', () => (
          <PaymentAccountsSettingsForm key={workspace?.id} />
        ))
        .with('billing', () => <BillingForm />)
        .with('integrations', () => (
          <IntegrationSettingsRouter workspaceId={workspaceId} />
        ))
        .with('user', () => (
          <div className="flex flex-col gap-f2">
            <Profile />
            <Preferences hideHeader />
          </div>
        ))
        .exhaustive(),
    [
      activeTab,
      refreshUserState,
      workspace?.id,
      workspaceId,
      isPaymentAccountsEnabled,
    ]
  );

  const onClose = useCallback(() => {
    if (pathMatch) {
      // If this was opened via the URL, navigate back to the projects
      // page without replacing history so browser "Back" works.
      navigate(`${base}/projects`);
    }
    handleClose();
  }, [navigate, pathMatch, handleClose, base]);
  return (
    <Modal
      data-testid="workspace-settings-modal"
      onClose={onClose}
      width={activeTab === 'integrations' ? 'lg' : 'md'}
      menu={
        <div className="flex flex-col h-full">
          <div className="text-main pb-f4">Settings</div>
          <List type="unordered">
            {availableWorkspaceTabs.map((tab, i) => (
              <ListItem
                key={tab}
                group
                onClick={
                  !billingTabEnabled && tab === 'billing'
                    ? undefined
                    : () => setActiveTab(tab)
                }
                extraClassNames={clsx(
                  !billingTabEnabled && tab === 'billing'
                    ? 'hover:cursor-disabled'
                    : 'hover:cursor-pointer'
                )}
              >
                <div className="flex flex-row space-x-[1ch]">
                  <Marker
                    type="numeric"
                    ordinal={i}
                    color={
                      tab === activeTab
                        ? 'text-main'
                        : 'text-main-500 group-hover:text-main'
                    }
                  />
                  <span
                    className={clsx(
                      tab === activeTab
                        ? 'text-main'
                        : 'text-main-500 group-hover:text-main'
                    )}
                  >
                    {match(tab)
                      .with('workspace', () => 'General')
                      .with('api-clients', () => 'API Clients')
                      .with('members', () => 'Members')
                      .with('data-exports', () => 'Data Exports')
                      .with('payment-accounts', () => 'Payments KYC')
                      .with('billing', () => 'Billing')
                      .with('user', () => 'User')
                      .with('integrations', () => 'Integrations')
                      .exhaustive()}
                  </span>
                </div>
              </ListItem>
            ))}
          </List>
        </div>
      }
      body={
        <div className="flex flex-col w-full min-w-f0 h-full min-h-f40 overflow-x-auto">
          <Suspense fallback={<LoadingText text="Loading" />}>
            {modalContent}
          </Suspense>
        </div>
      }
    />
  );
};
